// The rows a scoring board shows, and the words under them.
//
// Pure logic, deliberately apart from ScoreBoard.jsx: this is the product's
// explanation of a decision, so it is the part worth testing, and a .jsx file
// cannot be loaded by the test runner.
import { SCORE_TERMS, supplyWeightFactors } from './autoAssignScoring.js';
import { formatNumber } from './autoAssignLabels.js';

// Why the load or batching share on this card is not the one the sliders say:
// availability moved it. Only shown when it actually did.
const supplyNote = (key, facts, scoring) => {
	const factor = supplyWeightFactors(facts.supplyScarcity, scoring)[key];
	if (factor === undefined || factor === 1) return '';
	return ` · βαρύτητα ×${(Math.round(factor * 10) / 10).toLocaleString('el-GR')} λόγω διαθεσιμότητας (${formatNumber(facts.freeRiders)} ελεύθεροι / ${formatNumber(facts.waitingOrders)} περιμένουν)`;
};

// Where a criterion's points came from, in the units of the setting that
// produced them. Without it the score is a number to be taken on faith.
export const basisText = (key, facts, scoring) => {
	switch (key) {
		case 'idle':
			return facts.idleMinutes === null
				? 'δεν έχει ολοκληρώσει ποτέ παραγγελία'
				: `${formatNumber(Math.round(facts.idleMinutes))}λ σε κλίμακα ${formatNumber(Number(scoring.idleFullMinutes))}λ`;
		case 'distance': {
			// What the metres MEAN depends on how they were arrived at: a free rider
			// is measured plainly, a committed one by what the pickup adds to the
			// trip they are already on. Left unnamed, a rider "at 0 μ." who is
			// visibly across the map reads as a bug.
			const mode = facts.routeMode;
			const scale = `σε κλίμακα ${formatNumber(Number(scoring.distanceFullMeters))} μ.`;
			const base =
				mode === 'enroute'
					? `+${formatNumber(facts.meters)} μ. παράκαμψη στη διαδρομή του ${scale}`
					: mode === 'afterDrop'
						? `${formatNumber(facts.meters)} μ. αν περάσει αφού παραδώσει ${scale}`
						: mode === 'destinationOnly'
							? `${formatNumber(facts.meters)} μ. από το σημείο παράδοσής του ${scale}`
							: `${formatNumber(facts.meters)} μ. ${scale}`;
			const trust = facts.positionTrust ?? 1;
			// Below full trust the criterion's share is already reduced; saying so
			// here is what stops that looking like a mistake.
			return trust >= 1 ? base : `${base} · στίγμα ${Math.round(trust * 100)}% φρέσκο`;
		}
		case 'load': {
			const scale = `σε κλίμακα ${formatNumber(Number(scoring.loadFullOrders))}`;
			// The score reads effectiveLoad, so the basis has to as well — with
			// "Μία παραγγελία κάθε X μ." on, a rider holding 2 long deliveries is
			// scored as 3.4, and printing "2" under that number is simply a lie.
			const eff = facts.effectiveLoad;
			const inflated = Number.isFinite(eff) && Math.abs(eff - facts.liveOrders) > 0.05;
			const base = inflated
				? `${formatNumber(facts.liveOrders)} παραγγελίες → μετράνε σαν ${(Math.round(eff * 10) / 10).toLocaleString('el-GR')} λόγω μήκους διαδρομής ${scale}`
				: `${formatNumber(facts.liveOrders)} ${scale}`;
			return `${base}${supplyNote('load', facts, scoring)}`;
		}
		case 'batching': {
			const parts = facts.batchParts || [];
			if (!parts.length) return 'δεν κρατάει άλλη παραγγελία';
			// The worst sub-score is the one that decided it — `min` combines them —
			// so it is named rather than left to be worked out from three numbers.
			const worst = parts.reduce((a, b) => (b.score < a.score ? b : a));
			const label = {
				pickup: p => `καταστήματα ${formatNumber(p.meters)} μ.`,
				dropoff: p => `διευθύνσεις ${formatNumber(p.meters)} μ.`,
				ready: p => `ετοιμασίες ${Math.round(p.minutes)}λ`,
			};
			const all = parts
				.map(p => label[p.key]?.(p))
				.filter(Boolean)
				.join(' · ');
			return `${all} → κρίνει ${label[worst.key]?.(worst)}${
				facts.batchWithCarrying ? ' (την έχει ήδη παραλάβει)' : ''
			}${supplyNote('batching', facts, scoring)}`;
		}
		default:
			return '';
	}
};

// Why a criterion the admin has switched ON did not take part for this rider.
//
// Without this the board simply loses a row: someone sets four weights, sees two
// lines, and has no way to tell whether the other two were ignored, broken, or
// silently zero. Every reason here names the thing responsible.
const dropReason = (key, facts, scoring) => {
	const supply = supplyWeightFactors(facts.supplyScarcity, scoring);

	if (key === 'distance') {
		if (!(Number(scoring.distanceFullMeters) > 0)) {
			return 'η κλίμακα «Πιάνει 0 στα» είναι 0, οπότε δεν μπορεί να βαθμολογηθεί';
		}
		return 'δεν ξέρουμε πού βρίσκεται — άγνωστο ή πολύ παλιό στίγμα';
	}

	if (key === 'load') {
		if (!(Number(scoring.loadFullOrders) > 0)) {
			return 'η κλίμακα «Πιάνει 0 στις» είναι 0, οπότε δεν μπορεί να βαθμολογηθεί';
		}
		if (supply.load === 0) {
			return `η διαθεσιμότητα το μηδένισε (${formatNumber(facts.freeRiders)} ελεύθεροι / ${formatNumber(facts.waitingOrders)} περιμένουν)`;
		}
	}

	if (key === 'batching') {
		if (facts.batchFit === null || facts.batchFit === undefined) {
			return 'δεν κρατάει άλλη παραγγελία για να συνδυαστεί με αυτήν';
		}
		if (supply.batching === 0) {
			return `η διαθεσιμότητα το μηδένισε (${formatNumber(facts.freeRiders)} ελεύθεροι / ${formatNumber(facts.waitingOrders)} περιμένουν)`;
		}
	}

	return 'δεν υπήρχε στοιχείο για να μετρηθεί';
};

// The values that exclude rather than score. Shown even when the rider PASSED
// them: "I set a maximum distance — did it do anything?" is a question the board
// should answer without needing someone to be excluded first.
export const limitNotes = (facts, scoring, score) => {
	const out = [];

	const ceiling = Number(scoring.maxDistanceMeters);
	if (ceiling > 0) {
		out.push(
			facts.rawMeters === null || facts.rawMeters === undefined
				? `Όριο απόστασης ${formatNumber(ceiling)} μ.: δεν εφαρμόστηκε, δεν ξέρουμε πού είναι`
				: `Όριο απόστασης: ${formatNumber(facts.rawMeters)} μ. από ${formatNumber(ceiling)} μ. επιτρεπτά`,
		);
	}

	const floor = Number(scoring.minAssignScore);
	if (floor > 0) {
		out.push(`Ελάχιστος βαθμός για ανάθεση: ${score} από ${floor}`);
	}

	// The ceiling on orders in hand. Same reasoning as the two above: a limit you
	// set and then cannot see on the board is a limit you cannot tell is working.
	// It counts what the rider HOLDS — orders already on them plus anything this
	// pass gave them — so the comparison is against liveOrders, not against a
	// per-pass tally.
	const cap = Number(scoring.maxBatchSize);
	if (cap > 0 && Number.isFinite(Number(facts.liveOrders))) {
		out.push(`Το πολύ ${formatNumber(cap)} μαζί: κρατάει ${formatNumber(facts.liveOrders)}`);
	}

	// How stale a fix may get before the distance stops counting. Only worth a
	// line once it has actually started biting — at full trust it explains
	// nothing.
	const trust = facts.positionTrust ?? 1;
	const maxAge = Number(scoring.maxPositionAgeMinutes);
	if (maxAge > 0 && trust < 1) {
		out.push(
			trust <= 0
				? `Στίγμα παλιώνει σε ${formatNumber(maxAge)}λ: το δικό του δεν μετράει πια`
				: `Στίγμα παλιώνει σε ${formatNumber(maxAge)}λ: το δικό του μετράει ${Math.round(trust * 100)}%`,
		);
	}

	return out;
};

// Largest-remainder rounding, so the contributions shown add up to exactly the
// total shown. A panel whose whole job is to be believed cannot display three
// numbers that do not sum to the fourth.
const roundContributions = (raw, target) => {
	const floored = raw.map(v => Math.floor(v));
	let remaining = target - floored.reduce((a, b) => a + b, 0);

	const order = raw.map((v, i) => ({ i, frac: v - Math.floor(v) })).sort((x, y) => y.frac - x.frac);

	const out = [...floored];
	for (const { i } of order) {
		if (remaining <= 0) break;
		out[i] += 1;
		remaining -= 1;
	}
	return out;
};

// Turns one scoreCandidate result into the rows the board renders.
export const buildScoreParts = ({ scored, scoring, facts }) => {
	const used = scored.terms.filter(t => t.used);

	// Shares are against the weights that actually APPLIED to this rider, not
	// against every weight configured. The engine divides its weighted average by
	// the same sum, so a criterion that had nothing to say about someone — a
	// stale position, most often — redistributes its share across the rest rather
	// than quietly shrinking their total. Dividing by the configured total
	// instead makes the column stop adding up to the score.
	const usedWeight = used.reduce((sum, t) => sum + t.weight, 0);

	const labelOf = key => SCORE_TERMS.find(s => s.key === key)?.label || key;

	// Criteria the admin has switched on that did not take part for this rider.
	// They get a row too — a slider you moved and then cannot find on the board
	// is the single most confusing thing this panel can do.
	const dropped = scored.terms
		.filter(t => !t.used && Number(scoring[SCORE_TERMS.find(s => s.key === t.key)?.weightKey]) > 0)
		.map(t => ({
			key: t.key,
			label: labelOf(t.key),
			dropped: true,
			basis: dropReason(t.key, facts, scoring),
		}));

	// Nothing weighed in: the engine falls back to idle time alone, so say so
	// rather than showing a real total above an empty breakdown.
	if (!usedWeight) {
		const idle = scored.terms.find(t => t.key === 'idle');
		return [
			{
				key: 'idle',
				label: labelOf('idle'),
				score: idle?.score ?? scored.score,
				share: 100,
				contribution: scored.score,
				basis: basisText('idle', facts, scoring),
				fallback: true,
			},
			...dropped.filter(d => d.key !== 'idle'),
		];
	}

	// score × share is exactly this term's contribution to the weighted average,
	// so the column adds up to the total by construction.
	const contributions = roundContributions(
		used.map(t => (t.score * t.weight) / usedWeight),
		scored.score,
	);

	return [
		...used.map((t, i) => ({
			key: t.key,
			label: labelOf(t.key),
			score: t.score,
			share: Math.round((t.weight / usedWeight) * 100),
			contribution: contributions[i],
			basis: basisText(t.key, facts, scoring),
		})),
		...dropped,
	];
};

// Which row the board marks as the winner, and whether it should say the top is
// a tie instead.
//
// `winnerId` is the rider a REAL decision gave the order to, and it settles it.
// Recomputing from the scores is only right for the simulator, which has no
// recorded winner: scores tie often — «Απλά πράγματα» ranks on waiting time
// alone, so five riders sharing a score is an ordinary evening — and the reduce
// below keeps whichever of them the array happens to list first. On a stored
// decision that is a different rider from the one who got the order.
//
// Exported as a function rather than left inline in the component so it can be
// checked; a board that green-lights the wrong rider is worse than no board.
export const pickBoardWinner = ({ rows = [], winnerId } = {}) => {
	const inRunning = rows.filter(r => !r.excluded);

	const recorded =
		winnerId === null || winnerId === undefined
			? null
			: inRunning.find(r => `${r.id}` === `${winnerId}`);

	const best =
		recorded || (inRunning.length ? inRunning.reduce((a, b) => (b.score > a.score ? b : a)) : null);

	return {
		best,
		// A recorded winner settles it, so there is nothing left to call tied.
		tied:
			!recorded && !!best && inRunning.length > 1 && inRunning.every(r => r.score === best.score),
	};
};

// A simulator decision turned into board rows: the riders actually considered at
// that moment, with the same breakdown the sliders' example shows.
export const rowsFromVerdicts = ({ verdicts, driversById, scoring, skipReasonText }) =>
	(verdicts || []).map(v => {
		const driver = driversById[v.driverId];
		const base = { id: v.driverId, name: driver?.name || v.driverId };

		if (!v.eligible) {
			return {
				...base,
				excluded: true,
				excludedReason: skipReasonText(v.reason),
			};
		}

		const facts = v.scored?.facts || {};

		return {
			...base,
			score: v.scored?.score ?? 0,
			details: [
				facts.idleMinutes === null
					? 'δεν έχει ολοκληρώσει ποτέ παραγγελία'
					: `${Math.round(facts.idleMinutes)}λ χωρίς παραγγελία`,
				`${facts.liveOrders} ενεργές παραγγελίες`,
				facts.routeMode === 'enroute' || facts.routeMode === 'afterDrop'
					? `${formatNumber(facts.rawMeters)} μ. από το κατάστημα · λογίζεται ${formatNumber(facts.meters)} μ. λόγω της διαδρομής του`
					: facts.routeMode === 'destinationOnly'
						? `${formatNumber(facts.meters)} μ. από το σημείο παράδοσής του`
						: facts.meters === null || facts.meters === undefined
							? 'άγνωστη θέση'
							: `${formatNumber(facts.meters)} μ. από το κατάστημα`,
			],
			staleNote:
				(facts.positionTrust ?? 1) > 0
					? null
					: 'Δεν ξέρουμε πού βρίσκεται, οπότε η απόσταση δεν μετράει γι’ αυτόν.',
			parts: v.scored ? buildScoreParts({ scored: v.scored, scoring, facts }) : [],
			limits: v.scored ? limitNotes(facts, scoring, v.scored.score) : [],
		};
	});
